import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FileText, Plus, Clock, CheckCircle, AlertCircle, FilePlus, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const CitizenDashboard = () => {
  const { user } = useAuth();
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDemandes();
  }, []);

  const fetchDemandes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/demandes`, { withCredentials: true });
      setDemandes(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Reçue':
        return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">Reçue</span>;
      case 'En évaluation':
        return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">En évaluation</span>;
      case 'Clôturée':
        return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">Clôturée</span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">{status}</span>;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Espace Citoyen</h1>
          <p className="text-slate-500">Bienvenue, {user?.name}</p>
        </div>
        <Link to="/demander-aide">
          <Button className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="new-demande-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle demande
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{demandes.length}</div>
            <div className="text-sm font-medium text-slate-500">Dossiers totaux</div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{demandes.filter(d => d.status !== 'Clôturée').length}</div>
            <div className="text-sm font-medium text-slate-500">En cours</div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
            <CheckCircle className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{demandes.filter(d => d.status === 'Clôturée').length}</div>
            <div className="text-sm font-medium text-slate-500">Résolus</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
          <h2 className="text-lg font-bold text-slate-900">Mes démarches récentes</h2>
        </div>
        
        {loading ? (
          <div className="p-10 text-center text-slate-500">Chargement...</div>
        ) : demandes.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 text-slate-400">
              <FilePlus className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Aucune démarche</h3>
            <p className="text-slate-500 max-w-sm mx-auto mb-6 text-sm">
              Vous n'avez pas encore déposé de demande d'aide ou signalé de problème.
            </p>
              <Link to="/demander-aide">
                <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  Démarrer une démarche
                </Button>
              </Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {demandes.map(demande => (
              <div key={demande.id} className="p-5 hover:bg-slate-50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`demande-row-${demande.reference}`}>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-xs font-bold text-slate-500">#{demande.reference}</span>
                    {getStatusBadge(demande.status)}
                  </div>
                  <h4 className="font-semibold text-slate-900">{demande.sujet}</h4>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-1">{demande.description}</p>
                  {demande.fichier_joint && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-blue-600 bg-blue-50 w-fit px-2 py-1 rounded-md">
                      <FileText className="w-3 h-3" /> Pièce jointe
                    </div>
                  )}
                </div>
                <div className="flex sm:flex-col items-center sm:items-end justify-between gap-2">
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {new Date(demande.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <Link to={`/recipisse/${demande.id}`}>
                    <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 h-8">
                      Voir Récépissé
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CitizenDashboard;
